import React, { useContext, useEffect } from 'react';

import { EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import type { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type {
	EditorAppearance,
	PMPlugin,
	PMPluginFactoryParams,
	QuickInsertHandler,
} from '@atlaskit/editor-common/types';
import type { NodeType } from '@atlaskit/editor-prosemirror/model';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import { DecorationSet } from '@atlaskit/editor-prosemirror/view';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { SmartCardContext } from '@atlaskit/link-provider/context';
import type { CardContext } from '@atlaskit/link-provider/types';
import { isPromise } from '@atlaskit/mention/is-promise';
import { isResolvingMentionProvider } from '@atlaskit/mention/is-resolving-mention-provider';
import type { MentionProvider } from '@atlaskit/mention/types';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { UNSAFE_expValNoExposure } from '@atlaskit/platform-feature-experiments/unsafe-exp-val-no-exposure';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import { convertProsemirrorNodeToMarkdown } from '@atlassian/ai-model-io/convert-prosemirror-to-markdown';
import { createSkillAdf } from '@atlassian/conversation-assistant-content-transformer/utils/skills';
import { createUnifiedAnalyticsPayload } from '@atlassian/editor-ai-common/analytics/create-unified-analytics-payload';
import { normaliseAIExperienceEvent } from '@atlassian/editor-ai-common/utils/normaliseEvents';

import type { AIPlugin } from './aiPluginType';
import {
	commitInlineRovoStreaming,
	endAIExperience,
	endAISession,
	endInlineRovoStreaming,
	getPartialDocumentFromSelection,
	getPartialMarkdownFromSelection,
	isInlineEditTargetInView,
	openAIModal,
	recordAIContentSegments,
	recordOriginalNode,
	showInlineEditReady,
	stageInlineRovoStreaming,
	startAISession,
	tagAsAIStreamingTransformation,
	updateTargetId,
} from './editor-actions/actions';
import type { AllowedAIEvents, InsertedContentNodeTransformer } from './editor-actions/types';
import { insertContentCommand } from './editor-commands/insert-content-command';
import { createAgentEditChromePlugin } from './pm-plugins/agent-edit-chrome/plugin';
import { createAIAnnotationWatchmenPlugin } from './pm-plugins/ai-annotation-watchmen/ai-annotation-watchmen-plugin';
import { createAIAuditLogPlugin } from './pm-plugins/ai-audit-logs/ai-audit-log-plugin';
import { createAIButtonPlugin } from './pm-plugins/ai-button/ai-button-plugin';
import { aiButtonPluginKey } from './pm-plugins/ai-button/ai-button-plugin-key';
import { createAIEventHubPlugin } from './pm-plugins/ai-event-hub/ai-event-hub-plugin';
import { createAIMonitorWatchmenPlugin } from './pm-plugins/ai-monitor-watchmen/ai-monitor-watchmen-plugin';
import { createAiQuickPromptPlugins } from './pm-plugins/ai-quick-prompt/plugin';
import { createAIQuickstartPlugin } from './pm-plugins/ai-quickstart-command/ai-quickstart-command-plugin';
import {
	createAISessionContextPlugin,
	initialState as aiSessionInitialState,
} from './pm-plugins/ai-session-context/ai-session-context-plugin';
import { aiSessionContextPluginKey } from './pm-plugins/ai-session-context/ai-session-context-plugin-key';
import {
	clearAbortedToolCallPosition as clearAbortedToolCallPositionCommand,
	dispatchAllowedStreamingEvent,
} from './pm-plugins/ai-session-context/commands';
import { createAISmartButtonAPIPlugin } from './pm-plugins/ai-smart-button-api/ai-smart-button-api-plugin';
import { createSpaceShortcutPlugin } from './pm-plugins/ai-space-shortcut/ai-space-shortcut-plugin';
import { createDecorationPlugin } from './pm-plugins/decoration/decoration-plugin';
import { aiExperienceDecorationPluginKey } from './pm-plugins/decoration/decoration-plugin-key';
import type { AgentInfo } from './pm-plugins/decoration/reducer';
import {
	createDocumentContentStatePlugin,
	documentContentStatePluginKey,
	SET_CONSUMER_EMPTY_STATE_OVERRIDE,
} from './pm-plugins/document-content-state/document-content-state-plugin';
import { createGetEditorViewPlugin } from './pm-plugins/get-editor-view/get-editor-view-plugin';
import { createInEditorDirectStreamingPlugin } from './pm-plugins/in-editor-direct-streaming/in-editor-direct-streaming-plugin';
import { inEditorDirectStreamingPluginKey } from './pm-plugins/in-editor-direct-streaming/in-editor-direct-streaming-plugin-key';
import { createInlineSuggestionPlugin } from './pm-plugins/inline-suggestions/inline-suggestion-plugin';
import { createPlaceholderControllerPlugin } from './pm-plugins/placeholder-controller/placeholder-controller-plugin';
import { createInEditorDirectStreamingPlugin as createPurePmInEditorDirectStreamingPlugin } from './pm-plugins/pure-pm-in-editor-direct-streaming/plugin';
import { purePmInEditorDirectStreamingPluginKey } from './pm-plugins/pure-pm-in-editor-direct-streaming/pluginKey';
import { stampUnifiedAgentEditChrome } from './pm-plugins/pure-pm-in-editor-direct-streaming/transaction-builders/stampUnifiedAgentEditChrome';
import { rovoAgentsPluginKey } from './pm-plugins/rovo-agents/plugin-key';
import { createRovoAgentsPlugin } from './pm-plugins/rovo-agents/rovo-agents-plugin';
import { createSelectionPreviewPlugin } from './pm-plugins/selection-preview/selection-preview-plugin';
import { createSelectionPlugin } from './pm-plugins/selection/selection-plugin';
import { selectionPluginKey as aiExperienceSelectionPluginKey } from './pm-plugins/selection/selection-plugin-key';
import type { AgentAttribution } from './types/agent-attribution';
import type { EditorPluginAIInitAEP } from './types/analytics';
import { createAIBlockMenuComponents } from './ui/blockmenu/blockmenu';
import { ContentComponentWrapper } from './ui/components/ContentComponent/ContentComponentWrapper';
import { ContentComponentWrapper as ContentComponentWrapperLegacy } from './ui/components/ContentComponent/ContentComponentWrapperLegacy';
import { StreamingContentComponent } from './ui/components/ContentComponent/StreamingContentComponent';
import type { AnalyticsFlowBridgeRef } from './ui/components/SessionUI/AnalyticsBridge';
import { getAIPasteMenuComponents } from './ui/menus/ai-paste-menu';
import { CONFIG_ITEM_KEYS } from './ui/prebuilt/config-items/config-item-keys';
import { getVisibleEditorPluginAIConfigItems } from './ui/prebuilt/config-items/config-items';
import type { EditorPluginAIConfigItem } from './ui/prebuilt/config-items/config-items';
import { mapConfigItemsToQuickInsertItems } from './ui/prebuilt/config-items/utils/map-config-items-to-quick-insert-items';
import { getAIQuickInsertComponents } from './ui/quick-insert/getAIQuickInsertComponents';
import { getFloatingToolbarConfig } from './ui/toolbars/floating-toolbar';
import { getAIPrimaryToolbarComponent } from './ui/toolbars/primary-toolbar';
import { getPrimaryToolbarLegacyComponent } from './ui/toolbars/primary-toolbar-legacy';
import { getSelectionToolbarConfig } from './ui/toolbars/selection-toolbar';
import { AIToolbarComponents } from './ui/toolbars/toolbar';
import {
	getAskAIButton,
	getSmartLinkSummarizeButton,
} from './ui/toolbars/utils/floating-toolbar-button';
import { getIsAiEntryPointDisabled } from './ui/toolbars/utils/use-is-ai-entry-point-disabled';
import { aiActorCache } from './ui/utils/xstate/ai-actor-cache';
import { aiExperienceActorStore } from './ui/utils/xstate/get-ai-experience-service';
import type { AIExperienceMachineEvent } from './ui/utils/xstate/get-ai-experience-service';

type SmartCardClientRef = {
	current: CardContext['connections']['client'] | undefined;
};

/**
 * Bridges the SmartCard React context into plugin-registration code.
 * `isAsyncHidden` runs outside of React (registered once at plugin init),
 * so it cannot call hooks. This component keeps a mutable ref up-to-date
 * so the async check can reach `client.fetchData` without needing context access.
 */
const SmartCardClientSync = ({ clientRef }: { clientRef: SmartCardClientRef }) => {
	const smartCardContext = useContext(SmartCardContext);
	useEffect(() => {
		clientRef.current = smartCardContext?.connections?.client;
	}, [clientRef, smartCardContext?.connections?.client]);
	return null;
};

// eslint-disable-next-line @repo/internal/deprecations/deprecation-ticket-required -- Ignored via go/ED-25883
/**
 * Creates an AI plugin instance for the editor.
 *
 * @deprecated
 *
 * `NextEditorPluginAI` is deprecated and will be removed in a future release.
 * Please use the `aiPlugin` export instead.
 *
 * This temporary export exists to facilitate a smooth transition to the new plugin naming convention.
 *
 * @param root0 - The plugin configuration object
 * @param root0.config - The AI plugin configuration
 * @param root0.api - The editor API
 * @returns The AI plugin instance
 *
 * @example
 * ```ts
 * // Old import
 * import { NextEditorPluginAI } from '@atlassian/editor-plugin-ai/EditorPluginAI';
 *
 * // New import
 * import { aiPlugin } from '@atlassian/editor-plugin-ai';
 * ```
 */
export const NextEditorPluginAI: AIPlugin = ({ config, api }) => {
	const editorPluginAIProvider = config;
	const aiGlobalOptIn = config.aiGlobalOptIn;
	let editorView: EditorView | undefined;
	let insertedContentNodeTransformer: InsertedContentNodeTransformer | undefined;
	let beginInlineRovoEdit:
		| ((
				targetId: string,
		  ) => { attemptId: string; status: 'admitted' } | { status: 'legacy' | 'rejected' })
		| undefined;
	let reportInlineRovoEdit:
		| ((attemptId: string, outcome: 'generation-aborted' | 'generation-error') => boolean)
		| undefined;
	let startInlineRovoEditHandoffPresentation: ((attemptId: string) => boolean) | undefined;
	const setEditorView = (newEditorView: EditorView) => {
		editorView = newEditorView;
	};
	const isToolbarAIFCEnabled = Boolean(api?.toolbar);

	// Determine wehther or not a preview modal will be shown, if it is then the user must manually accept/reject the changes.
	// if canInlineStreamChanges() => true, then the preview will not be shown and the changes will be applied automatically
	// If the canInlineStreamChanges() => false, then the changes will not be applied automatically, and the user will have to accept/reject the changes.
	const canInlineStreamChanges = () => {
		// The plugin can be passed a user prefs callback which could contain a setting which would disable in-document streaming.
		// We will default to true if this setting is not provided.
		const { isInDocumentStreamingAllowed = true } =
			editorPluginAIProvider?.getUserPreferences?.() ?? {};

		return (
			// We only allow auto-accepting AI changes if the in-document streaming feature flag is enabled
			!!editorPluginAIProvider.isInDocumentStreamingEnabled && isInDocumentStreamingAllowed
		);
	};

	let providerFactory: ProviderFactory;
	const setProviderFactory = (newProviderFactory: ProviderFactory) => {
		providerFactory = newProviderFactory;
	};

	let mentionProvider: MentionProvider | undefined;

	let analyticsBridgeRef: AnalyticsFlowBridgeRef | null = null;

	/**
	 * Helper function to retrieve the name details for a mention from the mentionProvider
	 * @param id - The mention ID to resolve
	 * @returns The mention name details or undefined if not found
	 * @example
	 * ```ts
	 * const mentionDetails = await getMentionNameDetails('user-123');
	 * ```
	 */
	const getMentionNameDetails = async (id: string) => {
		if (!mentionProvider || !isResolvingMentionProvider(mentionProvider)) {
			return;
		}

		const nameDetails = mentionProvider.resolveMentionName(id);

		if (isPromise(nameDetails)) {
			const mention = await nameDetails;
			return mention;
		}

		return nameDetails;
	};

	const setMentionProvider = () => {
		const handler = (_name: string, providerPromise?: Promise<MentionProvider>) => {
			if (providerPromise) {
				providerPromise.then((provider) => {
					mentionProvider = provider;
					providerFactory.unsubscribe('mentionProvider', handler);
				});
			}
		};
		providerFactory.subscribe('mentionProvider', handler);
	};

	const setMediaProvider = () => {
		if (editorPluginAIProvider?.providers?.mediaProvider) {
			providerFactory.setProvider(
				'mediaProvider',
				Promise.resolve(editorPluginAIProvider.providers.mediaProvider),
			);
		}
	};

	const setEmojiProvider = () => {
		const { emojiProvider } = api?.emoji?.sharedState.currentState() || {};
		if (!!emojiProvider) {
			providerFactory.setProvider('emojiProvider', Promise.resolve(emojiProvider));
		} else {
			const unsub = api?.emoji?.sharedState.onChange((sharedState) => {
				if (!!sharedState.nextSharedState?.emojiProvider) {
					providerFactory.setProvider(
						'emojiProvider',
						Promise.resolve(sharedState.nextSharedState.emojiProvider),
					);
					unsub?.();
				}
			});
		}
	};

	const setExtensionProvider = () => {
		const { extensionProvider } = api?.extension?.sharedState.currentState() || {};
		if (!!extensionProvider) {
			providerFactory.setProvider('extensionProvider', Promise.resolve(extensionProvider));
		} else {
			const unsub = api?.extension?.sharedState.onChange((sharedState) => {
				if (!!sharedState.nextSharedState?.extensionProvider) {
					providerFactory.setProvider(
						'extensionProvider',
						Promise.resolve(sharedState.nextSharedState.extensionProvider),
					);
					unsub?.();
				}
			});
		}
	};

	const aiOptIn = aiGlobalOptIn || {
		status: 'enabled',
		triggerOptInFlow: () => {},
	};

	const isFullEditorAIExperience = editorPluginAIProvider.experienceMode !== 'streaming-only';

	const isRovoAvailable =
		editorPluginAIProvider.isRovoEnabled === true ||
		editorPluginAIProvider.aiGlobalOptIn?.status === 'disabled-opt-in';
	const isRegisteredSlashCommandEnabled = isExperimentEnabled('platform_editor_slash_command');

	const quickInsert: QuickInsertHandler = (intl) => {
		if (getIsAiEntryPointDisabled(api)) {
			return [];
		}

		const configItems: EditorPluginAIConfigItem[] = [];

		if (!editorPluginAIProvider.disableQuickInsert) {
			if (editorPluginAIProvider.isInDocumentStreamingEnabled) {
				if (isRovoAvailable) {
					configItems.push(editorPluginAIProvider.baseGenerate);
				}
			} else {
				configItems.push(
					...(isRovoAvailable ? [editorPluginAIProvider.baseGenerate] : []),
					...getVisibleEditorPluginAIConfigItems({
						configItemWithOptions: editorPluginAIProvider.configItemWithOptions,
						// The editor view will always be available when quick insert items are being created
						// Ignored via go/ees005
						// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
						editorView: editorView!,
						positions: [0, 0],
						isEmpty: true,
					}),
				);
			}
		}

		const quickInsertItems = mapConfigItemsToQuickInsertItems({
			configItems,
			formatMessage: intl.formatMessage,
			aiGlobalOptIn: aiOptIn,
			// The editor view will always be available when quick insert items are being created
			// Ignored via go/ees005
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			editorView: editorView!,
			locale: intl.locale,
		});

		return quickInsertItems;
	};

	quickInsert.disableMemo = true;

	if (
		isFullEditorAIExperience &&
		!editorPluginAIProvider.disableQuickInsert &&
		isRegisteredSlashCommandEnabled
	) {
		api?.uiControlRegistry?.actions.register(
			getAIQuickInsertComponents({
				aiGlobalOptIn: aiOptIn,
				api,
				editorPluginAIProvider,
				getEditorView: () => editorView,
				isRovoAvailable,
			}),
		);
	}

	if (isFullEditorAIExperience && isToolbarAIFCEnabled) {
		const shouldShowImproveDescription =
			config.configItemWithOptions?.[0]?.config?.key === CONFIG_ITEM_KEYS.ADD_STRUCTURE;
		api?.toolbar?.actions.registerComponents(
			AIToolbarComponents({
				aiGlobalOptIn: aiOptIn,
				editorPluginAIProvider,
				shouldShowImproveDescription,
				getAdditionalProps: config.getAdditionalProps,
				isNewToolbarEnabled: true,
				api,
				isRovoAvailable,
			}),
		);
	} else if (isFullEditorAIExperience) {
		if (!editorPluginAIProvider?.hidePrimaryToolbarButton) {
			api?.primaryToolbar?.actions.registerComponent({
				name: 'aiExperience',
				component: getAIPrimaryToolbarComponent({
					api,
					editorPluginAIProvider,
					aiGlobalOptIn: aiOptIn,
				}),
			});
		}
	}

	if (isFullEditorAIExperience && fg('platform_editor_ai_in_blockmenu')) {
		api?.blockMenu?.actions.registerBlockMenuComponents(
			createAIBlockMenuComponents({
				editorPluginAIProvider,
				getEditorView: () => editorView,
				api,
			}),
		);
	}

	// Tracks the live editor appearance so paste-menu rules can read it lazily.
	// Updated on every contentComponent render (which the framework calls whenever
	// appearance changes) so no product plumbing is needed.
	let currentAppearance: EditorAppearance | undefined;

	// Use a ref to store the client as SmartCardContext is only available on through hooks
	// 		and this plugin is outside of a React component.
	const aiSmartCardClientRef: SmartCardClientRef = { current: undefined };

	const shouldRegisterAIPasteMenu =
		editorPluginAIProvider?.isInDocumentStreamingEnabled &&
		isFullEditorAIExperience &&
		expValEqualsNoExposure('platform_editor_paste_actions_menu', 'isEnabled', true);

	if (shouldRegisterAIPasteMenu) {
		api?.uiControlRegistry?.actions.register(
			getAIPasteMenuComponents({
				aiGlobalOptIn: aiOptIn,
				api,
				getAppearance: () => currentAppearance ?? api?.core?.sharedState.currentState()?.appearance,
				product: editorPluginAIProvider.product,
				smartCardClientRef: aiSmartCardClientRef,
			}),
		);
	}

	return {
		name: 'aiExperience',
		pmPlugins: () => {
			const plugins: PMPlugin[] = [
				{
					name: 'ai-content-state',
					plugin: () =>
						createDocumentContentStatePlugin(
							editorPluginAIProvider.initialConsumerEmptyStateOverride,
						),
				},
				{
					name: 'ai-experience-actor',
					plugin: () =>
						new SafePlugin({
							view: (editorView) => {
								// This sets up an [actor](https://xstate.js.org/docs/guides/actors.html#actor-api) using
								// the editor-plugin-ai machine and starts it up.
								const editorPluginAIActor = aiActorCache.getActor(editorView);
								return {
									destroy: () => {
										// This cleansup any listeners from the actor -- to ensure we don't leak memory.
										editorPluginAIActor?.stop();
										aiActorCache.delete(editorView);
									},
								};
							},
						}),
				},
				{
					name: 'aiExperience',
					plugin: (options: PMPluginFactoryParams) => {
						const payload: EditorPluginAIInitAEP = {
							action: 'init',
							actionSubject: 'editor',
							actionSubjectId: 'editorPluginAI',
							eventType: EVENT_TYPE.TRACK,
							attributes: {
								aiGlobalOptIn: aiOptIn?.status,
							},
						};
						// @ts-expect-error - This prevent type errors when trying to pass EditorPluginAI payload to AnalyticsEventPayload
						options.dispatchAnalyticsEvent(payload);

						return createDecorationPlugin({
							dispatchAnalyticsEvent: options.dispatchAnalyticsEvent,
							dispatch: options.dispatch,
							api,
							isInDocumentStreamingEnabled: editorPluginAIProvider.isInDocumentStreamingEnabled,
							product: editorPluginAIProvider.product,
						});
					},
				},
				{
					name: 'ai-experience-get-editor-view-and-provider-factory',
					plugin: (options: PMPluginFactoryParams) => {
						setProviderFactory(options.providerFactory);
						setMentionProvider();
						setMediaProvider();
						setEmojiProvider();
						setExtensionProvider();
						return createGetEditorViewPlugin({
							setEditorView: setEditorView,
						});
					},
				},
			];

			if (isFullEditorAIExperience) {
				plugins.push(
					{
						name: 'ai-experience-ai-button',
						plugin: () => createAIButtonPlugin(),
					},
					{
						name: 'ai-experience-selection-preview',
						plugin: () => createSelectionPreviewPlugin(),
					},
				);
			}

			if (expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true)) {
				plugins.push({
					name: 'ai-session-context',
					plugin: ({ dispatch, getIntl }: PMPluginFactoryParams) =>
						createAISessionContextPlugin({
							aiGlobalOptIn: aiOptIn,
							api,
							dispatch,
							editorPluginAIProvider,
							getIntl,
							getMentionNameDetails,
						}),
				});
			}

			if (
				isFullEditorAIExperience &&
				(editorPluginAIProvider.product === 'CONFLUENCE' ||
					editorPluginAIProvider.product === 'JSW' ||
					editorPluginAIProvider.product === 'JSM' ||
					editorPluginAIProvider.product === 'JPD' ||
					editorPluginAIProvider.product === 'JWM')
			) {
				plugins.push({
					name: 'inline-suggestions',
					plugin: ({ dispatch, getIntl }: PMPluginFactoryParams) =>
						createInlineSuggestionPlugin({
							dispatch,
							getIntl,
							api,
						}),
				});
			}

			if (editorPluginAIProvider.isInDocumentStreamingEnabled) {
				plugins.push({
					name: 'ai-experience-placeholder-controller',
					plugin: () => createPlaceholderControllerPlugin({ api, editorPluginAIProvider }),
				});
			}

			plugins.push({
				name: 'ai-event-hub',
				plugin: ({ getIntl }: PMPluginFactoryParams) =>
					createAIEventHubPlugin({
						aiGlobalOptIn: aiOptIn,
						editorPluginAIProvider,
						getIntl,
						api,
					}),
			});

			if (isFullEditorAIExperience && editorPluginAIProvider.isRovoEnabled) {
				plugins.push({
					name: 'editor-ai-rovo-agents',
					plugin: (options: PMPluginFactoryParams) => createRovoAgentsPlugin(options),
				});
				plugins.push({
					name: 'ai-experience-selection',
					plugin: () =>
						createSelectionPlugin({
							api,
							editorPluginAIProvider,
						}),
				});
			}

			if (
				editorPluginAIProvider.auditLogSettings &&
				editorPluginAIProvider.auditLogSettings.enabled &&
				fg('platform_editor_ai_audit_logs_events')
			) {
				const eventData = editorPluginAIProvider.auditLogSettings.eventData;
				plugins.push({
					name: 'ai-audit-log',
					plugin: ({ dispatch }: PMPluginFactoryParams) =>
						createAIAuditLogPlugin({
							dispatch,
							eventData,
						}),
				});
			}

			const { aiSmartButtonOptions, dynamicSurfaceConfigItems } = editorPluginAIProvider;
			if (
				isFullEditorAIExperience &&
				aiSmartButtonOptions &&
				dynamicSurfaceConfigItems &&
				fg('ai-smart-button-team-25')
			) {
				plugins.push({
					name: 'ai-smart-button-api',
					plugin: ({ getIntl, dispatch }: PMPluginFactoryParams) =>
						createAISmartButtonAPIPlugin({
							aiGlobalOptIn: aiOptIn,
							aiSmartButtonOptions,
							api,
							dispatch,
							dynamicSurfaceConfigItems,
							getIntl,
							product: editorPluginAIProvider.product,
							getMentionNameDetails,
						}),
				});
			}

			if (isFullEditorAIExperience) {
				plugins.push({
					name: 'ai-quickstart-command',
					plugin: ({ getIntl }: PMPluginFactoryParams) =>
						createAIQuickstartPlugin({
							aiGlobalOptIn: aiOptIn,
							editorPluginAIProvider,
							getIntl,
						}),
				});
			}

			if (editorPluginAIProvider.isInDocumentStreamingEnabled) {
				const canAutoAcceptAIChanges = canInlineStreamChanges();
				if (expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true)) {
					plugins.push({
						name: 'pure-pm-in-editor-direct-streaming',
						plugin: ({ getIntl, portalProviderAPI }: PMPluginFactoryParams) =>
							createPurePmInEditorDirectStreamingPlugin({
								getIntl,
								api,
								canAutoAcceptAIChanges,
								getFireAnalyticsStep: () => analyticsBridgeRef?.current?.fireAnalyticsStep,
								portalProviderAPI,
								streamingTelepointerIdentity: editorPluginAIProvider.streamingTelepointerIdentity,
							}),
					});
				} else {
					plugins.push({
						name: 'in-editor-direct-streaming',
						plugin: ({ getIntl, portalProviderAPI }: PMPluginFactoryParams) =>
							createInEditorDirectStreamingPlugin({
								getIntl,
								api,
								portalProviderAPI,
								streamingTelepointerIdentity: editorPluginAIProvider.streamingTelepointerIdentity,
							}),
					});
				}

				plugins.push({
					name: 'ai-annotation-watchmen',
					plugin: () =>
						createAIAnnotationWatchmenPlugin({
							onAnnotationsRemoved: editorPluginAIProvider.onAnnotationsRemoved,
						}),
				});

				plugins.push({
					name: 'ai-monitor-watchmen',
					plugin: () => createAIMonitorWatchmenPlugin(api, canInlineStreamChanges),
				});

				/**
				 * We don't want to show the space shortcut plugin if the user has opted out of AI
				 * This will prevent admins who opted out of AI from triggering the opt in flow
				 * with the space shortcut.
				 */
				if (
					isFullEditorAIExperience &&
					editorPluginAIProvider.product === 'CONFLUENCE' &&
					aiOptIn?.status === 'enabled' &&
					isRovoAvailable
				) {
					plugins.push({
						name: 'ai-space-shortcut',
						plugin: ({ getIntl }) =>
							createSpaceShortcutPlugin({
								aiGlobalOptIn: aiOptIn,
								editorPluginAIProvider,
								getIntl,
							}),
					});
				}

				if (
					isFullEditorAIExperience &&
					editorPluginAIProvider.enableQuickPrompt &&
					(fg('platform_editor_ai_quick_prompt_iw_cc_load_2')
						? aiOptIn?.status === 'enabled'
						: true) &&
					fg('platform_editor_ai_quick_prompt_iw_cc_load')
				) {
					plugins.push(
						...createAiQuickPromptPlugins({
							aiGlobalOptIn: aiOptIn,
							api,
							product: editorPluginAIProvider.product,
						}),
					);
				}

				if (
					isExperimentEnabled('platform_editor_ai_unified_post_apply_chrome') ||
					isExperimentEnabled('platform_editor_ai_streaming_ux_experience_m1')
				) {
					plugins.push({
						name: 'agent-edit-chrome',
						plugin: ({ getIntl, portalProviderAPI }: PMPluginFactoryParams) =>
							createAgentEditChromePlugin({
								api,
								getIntl,
								portalProviderAPI,
								streamingTelepointerIdentity: editorPluginAIProvider.streamingTelepointerIdentity,
							}),
					});
				}
			}

			return plugins;
		},

		actions: {
			beginInlineRovoEdit: (targetId: string) =>
				beginInlineRovoEdit?.(targetId) ?? { status: 'legacy' },
			reportInlineRovoEdit: (attemptId, outcome) =>
				reportInlineRovoEdit?.(attemptId, outcome) ?? false,
			startInlineRovoEditHandoffPresentation: (attemptId) =>
				startInlineRovoEditHandoffPresentation?.(attemptId) ?? false,
			registerInlineRovoEditAdmission: (begin) => {
				beginInlineRovoEdit = begin;
				return () => {
					if (beginInlineRovoEdit === begin) {
						beginInlineRovoEdit = undefined;
					}
				};
			},
			registerInlineRovoEditReporting: (report) => {
				reportInlineRovoEdit = report;
				return () => {
					if (reportInlineRovoEdit === report) {
						reportInlineRovoEdit = undefined;
					}
				};
			},
			registerInlineRovoEditHandoffPresentation: (start) => {
				startInlineRovoEditHandoffPresentation = start;
				return () => {
					if (startInlineRovoEditHandoffPresentation === start) {
						startInlineRovoEditHandoffPresentation = undefined;
					}
				};
			},
			openAIModal: ({
				invokedFrom,
				configItem = editorPluginAIProvider.baseGenerate,
				withAcceptReject,
				withStreamingHighlight,
				skipSelectionHighlight,
				hideLoadingScreen,
				traceId,
				singleInstrumentationId,
				isCreate,
				promptVersion,
				userADFInput,
				overrideFromPos,
				overrideToPos,
			}) => {
				openAIModal({
					invokedFrom,
					editorView,
					aiOptIn,
					configItem,
					withAcceptReject,
					withStreamingHighlight,
					skipSelectionHighlight,
					isExternal: true,
					hideLoadingScreen,
					traceId,
					singleInstrumentationId,
					isCreate,
					promptVersion,
					userADFInput,
					// Forward override positions so a resumed (aborted) tool call re-applies
					// at its stored/mapped range, not the current selection / end of doc.
					overrideFromPos,
					overrideToPos,
				});
			},
			handleSkillSelected: (slashCommand, skill) => {
				if (!editorView) {
					return;
				}
				openAIModal({
					invokedFrom: 'quickInsert',
					editorView,
					aiOptIn,
					configItem: editorPluginAIProvider.baseGenerate,
					isExternal: true,
					userADFInput: createSkillAdf(skill),
				});
			},
			endAIExperience: () => {
				endAIExperience({ editorView });
			},
			getAbortedToolCallPosition: (toolCallId: string) => {
				if (!editorView) {
					return undefined;
				}
				const sessionState = aiSessionContextPluginKey.getState(editorView.state);
				return sessionState?.abortedToolCallPositions?.[toolCallId];
			},
			clearAbortedToolCallPosition: (toolCallId: string) => {
				if (!editorView) {
					return;
				}
				const command = clearAbortedToolCallPositionCommand(toolCallId);
				const tr = command({ tr: editorView.state.tr });
				if (tr) {
					editorView.dispatch(tr);
				}
			},
			getAiExperienceActor: () => {
				if (!editorView) {
					throw new Error('editorView is not defined');
				}

				const actor = aiExperienceActorStore.getActor(editorView);
				if (!actor) {
					throw new Error('No cached item found');
				}

				return actor;
			},
			getAISelectionContext: () => {
				const selection = api?.selection?.sharedState.currentState()?.selection;
				const selectionContent = selection?.content().content;

				const selectionFragment = api?.selection?.actions.getSelectionFragment() ?? null;
				const additionalNodeContext =
					api?.aiSelectionContext?.actions.getAdditionalContextForFragments(
						selectionFragment ?? [],
					);

				return (
					api?.aiExperience?.sharedState.currentState()?.selectionContext ?? {
						selectionFragment,
						localIds: api?.selection?.actions.getSelectionLocalIds() ?? null,
						selectionMarkdown: selectionContent
							? convertProsemirrorNodeToMarkdown({
									node: selectionContent,
									featureToggles: {
										markdownPlusSanitizeProseMirrorOnlyNodes: fg(
											'platform_editor_markdown_plus_sanitize_pm_nodes',
										),
									},
								})?.markdown
							: null,
						startIndex: selection?.$from.parentOffset ?? null,
						endIndex: selection?.$to.parentOffset ?? null,
						additionalNodeContext,
					}
				);
			},
			getPartialDocumentFromSelection: (maxSize) => {
				return getPartialDocumentFromSelection({
					selection: editorView?.state.selection,
					maxSize,
				});
			},
			getPartialMarkdownFromSelection: (maxSize) => {
				return getPartialMarkdownFromSelection({
					selection: editorView?.state.selection,
					maxSize,
				});
			},
			getAskRovoFloatingToolbarButton: (options) => {
				// We don't support ask rovo actions on other products just yet
				if (
					config.product.toLowerCase() !== 'confluence' &&
					!(
						config.product.toLowerCase() === 'jsw' &&
						['image', 'remix-app'].includes(options.target ?? '') &&
						fg('platform_editor_maui_jira_updates_1')
					) &&
					fg('platform_editor_maui_jira_updates')
				) {
					return undefined;
				}
				return getAskAIButton({
					intl: options.intl,
					rangeBaseGenerate: editorPluginAIProvider.baseGenerate,
					aiGlobalOptIn: aiOptIn,
					triggeredFor: options.triggeredFor,
					disabled: options.disabled ?? false,
					openInRovoChat: options.openInRovoChat,
					rovoChatAIFeatureContext: options.rovoChatAIFeatureContext,
					target: options.target,
				});
			},
			getSummariseSmartlinkFloatingToolbarButton: (options) => {
				return getSmartLinkSummarizeButton({
					api,
					intl: options.intl,
					aiGlobalOptIn: aiOptIn,
					smartlinkUrl: options.smartlinkUrl,
					nodeType:
						options.triggeredFor ??
						editorView?.state.schema.nodes.extension ??
						({} as unknown as NodeType),
					disabled: options.disabled ?? false,
				});
			},
			startSession: (
				sessionId: string,
				traceId?: string,
				promptVersion?: string,
				agentInfo?: AgentInfo,
				isCreateSession?: boolean,
				isInlineEditSession?: boolean,
				agentAttribution?: AgentAttribution,
				isChatEditSession?: boolean,
				sourceMessageId?: string,
			) => {
				api?.core?.actions.execute(api?.history?.commands?.startHistorySlice('ai'));
				startAISession({
					editorView,
					sessionId,
					traceId: traceId,
					promptVersion: fg('platform_editor_ai_promptversion_in_analytics')
						? promptVersion
						: undefined,
					agentInfo,
					isCreateSession,
					isInlineEditSession,
					agentAttribution,
					isChatEditSession,
					sourceMessageId,
				});
			},
			endSession: (sessionId: string, metadata: { type: 'abort' | 'finished' | 'error' }) => {
				// [CCI-15904] Pass `editorPluginAIProvider.baseGenerate` + `aiOptIn` so
				// that, when the New AIFC Editor Experience is enabled and the session
				// ended cleanly, `endAISession` can auto-open the Post Stream Review
				// modal through the canonical `openAIModal` entry point.
				endAISession({
					api,
					editorView,
					sessionId,
					metadata,
					baseGenerate: editorPluginAIProvider.baseGenerate,
					aiOptIn,
				});
				api?.core?.actions.execute(api?.history?.commands?.endHistorySlice('ai'));
			},
			updateTargetId: (targetId: string | number) => {
				updateTargetId({ editorView, targetId });
			},
			stageInlineRovoStreaming: (targetId: string) => {
				stageInlineRovoStreaming({ editorView, targetId });
			},
			commitInlineRovoStreaming: () => {
				commitInlineRovoStreaming({ editorView });
			},
			endInlineRovoStreaming: () => {
				endInlineRovoStreaming({ editorView });
			},
			showInlineEditReady: (targetId?: string) => {
				showInlineEditReady({ editorView, targetId });
			},
			isInlineEditTargetInView: (targetId?: string) => {
				return isInlineEditTargetInView({ editorView, targetId });
			},
			dispatchAIEvent: (event: AllowedAIEvents) => {
				if (expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true)) {
					api?.core.actions.execute(dispatchAllowedStreamingEvent(event));
				} else {
					const normalisedEvent = normaliseAIExperienceEvent(event);
					api?.aiExperience?.actions
						.getAiExperienceActor()
						?.send(normalisedEvent as AIExperienceMachineEvent);
				}
			},
			stampAgentEditChrome: (tr) => {
				return editorView ? stampUnifiedAgentEditChrome(tr, editorView.state) : tr;
			},
			tagAsAIStreamingTransformation,
			recordAIContentSegments,
			recordOriginalNode,
			registerInsertedContentNodeTransformer: (transformer: InsertedContentNodeTransformer) => {
				insertedContentNodeTransformer = transformer;
			},
			getInsertedContentNodeTransformer: () => insertedContentNodeTransformer,
			fireUnifiedAnalyticsEvent: (payload: Parameters<typeof createUnifiedAnalyticsPayload>[0]) => {
				api?.analytics?.actions.fireAnalyticsEvent(createUnifiedAnalyticsPayload(payload));
			},
			setAnalyticsBridgeRef: (ref: AnalyticsFlowBridgeRef | null) => {
				analyticsBridgeRef = ref;
			},
			toggleAiEntryPoints: (disable?: boolean) => {
				if (!editorView) {
					return;
				}

				const currentState =
					api?.aiExperience?.sharedState.currentState()?.areAiEntryPointsDisabled;
				const nextDisabledState = disable ?? !currentState;
				const tr = editorView.state.tr.setMeta('toggleAiEntryPoints', nextDisabledState);
				editorView.dispatch(tr);
			},
			setConsumerEmptyStateOverride: (value: boolean | undefined) => {
				if (!editorView || !UNSAFE_expValNoExposure('jira-smart-replies-v2', 'isEnabled', false)) {
					return;
				}
				const tr = editorView.state.tr.setMeta(documentContentStatePluginKey, {
					type: SET_CONSUMER_EMPTY_STATE_OVERRIDE,
					value,
				});
				editorView.dispatch(tr);
			},
		},
		commands: {
			insertContent: (payload, positions) => {
				if (!editorView) {
					throw new Error('editorView is not defined');
				}
				// Ensure the editor is focused so when we insert it scrolls to the
				// insertion
				api?.core.actions.focus({ scrollIntoView: false });
				return insertContentCommand({
					editorView,
					api,
					insertedContentNodeTransformer,
					payload,
					positions,
					scrollIntoView: fg('platform_editor_ai_remix_in_editor_loading') ? false : undefined,
				});
			},
		},
		getSharedState(state: EditorState | undefined) {
			if (!state) {
				return {
					areAiEntryPointsDisabled: false,
					nonCursorSelectionChangeCount: 0,
					aiButtonState: {
						triggeredFor: undefined,
						isSelectionValidForAIExperience: true,
					},
					aiDecorationExperiencePluginState: {
						agentInfo: undefined,
						isRunning: false,
						interactionSessionId: undefined,
						modalDecorationSet: DecorationSet.empty,
						configItem: undefined,
						lastTriggeredFrom: undefined,
						modalMountedTimeStamp: undefined,
						triggerMethod: undefined,
						initialPrompt: undefined,
						userADFInput: undefined,
						willAISuggestEmoji: undefined,
						triggeredFor: undefined,
						initialPositions: undefined,
						nudgeMetrics: undefined,
						hideLoadingScreen: false,
						targetId: undefined,
						inlineRovoStreamingTargetId: null,
						pendingInlineRovoStreamingTargetId: null,
						isInlineEditReady: false,
						inlineEditReadyTargetId: undefined,
						isInDocumentStreamingEnabled: editorPluginAIProvider.isInDocumentStreamingEnabled,
						traceId: undefined,
						product: editorPluginAIProvider.product,
					},
					rovoAgentsPluginState: {
						positions: undefined,
						isBrowseModalOpen: false,
					},
					isStreaming: false,
					isShowingAcceptRejectScreen: false,
					chunkNumber: 0,
					selectionContext: undefined,
					hasVisibleContent: false,
					consumerEmptyStateOverride: undefined,
					aiSessionPluginState: aiSessionInitialState,
				};
			}

			return {
				areAiEntryPointsDisabled:
					(aiButtonPluginKey.getState(state) ?? {}).areAiEntryPointsDisabled ?? false,
				nonCursorSelectionChangeCount: (aiExperienceSelectionPluginKey.getState(state) ?? {})
					?.nonCursorSelectionChangeCount,
				aiButtonState: {
					triggeredFor: (aiButtonPluginKey.getState(state) ?? {}).triggeredFor,
					isSelectionValidForAIExperience: (aiButtonPluginKey.getState(state) ?? {})
						.isSelectionValidForAIExperience,
				},
				aiDecorationExperiencePluginState: {
					agentInfo: (aiExperienceDecorationPluginKey.getState(state) ?? {}).agentInfo,
					isRunning: (aiExperienceDecorationPluginKey.getState(state) ?? {}).isRunning ?? false,
					modalDecorationSet:
						(aiExperienceDecorationPluginKey.getState(state) ?? {}).modalDecorationSet ??
						DecorationSet.empty,
					configItem: (aiExperienceDecorationPluginKey.getState(state) ?? {}).configItem,
					lastTriggeredFrom: (aiExperienceDecorationPluginKey.getState(state) ?? {})
						.lastTriggeredFrom,
					modalMountedTimeStamp: (aiExperienceDecorationPluginKey.getState(state) ?? {})
						.modalMountedTimeStamp,
					triggerMethod: (aiExperienceDecorationPluginKey.getState(state) ?? {}).triggerMethod,
					initialPrompt: (aiExperienceDecorationPluginKey.getState(state) ?? {}).initialPrompt,
					userADFInput: (aiExperienceDecorationPluginKey.getState(state) ?? {}).userADFInput,
					willAISuggestEmoji: (aiExperienceDecorationPluginKey.getState(state) ?? {})
						.willAISuggestEmoji,
					triggeredFor: (aiExperienceDecorationPluginKey.getState(state) ?? {}).triggeredFor,
					initialPositions: (aiExperienceDecorationPluginKey.getState(state) ?? {})
						.initialPositions,
					positions: (aiExperienceDecorationPluginKey.getState(state) ?? {}).positions,
					nudgeMetrics: (aiExperienceDecorationPluginKey.getState(state) ?? {}).nudgeMetrics,
					currentSessionId: (aiExperienceDecorationPluginKey.getState(state) ?? {})
						.currentSessionId,
					nextSessionId: (aiExperienceDecorationPluginKey.getState(state) ?? {}).nextSessionId,
					sessionStarted: (aiExperienceDecorationPluginKey.getState(state) ?? {}).sessionStarted,
					interactionSessionId: (aiExperienceDecorationPluginKey.getState(state) ?? {})
						.interactionSessionId,
					hideLoadingScreen: (aiExperienceDecorationPluginKey.getState(state) ?? {})
						.hideLoadingScreen,
					targetId: (aiExperienceDecorationPluginKey.getState(state) ?? {}).targetId,
					inlineRovoStreamingTargetId:
						(aiExperienceDecorationPluginKey.getState(state) ?? {}).inlineRovoStreamingTargetId ??
						null,
					pendingInlineRovoStreamingTargetId:
						(aiExperienceDecorationPluginKey.getState(state) ?? {})
							.pendingInlineRovoStreamingTargetId ?? null,
					isInlineEditReady:
						(aiExperienceDecorationPluginKey.getState(state) ?? {}).isInlineEditReady ?? false,
					inlineEditReadyTargetId: (aiExperienceDecorationPluginKey.getState(state) ?? {})
						.inlineEditReadyTargetId,
					isInDocumentStreamingEnabled: (aiExperienceDecorationPluginKey.getState(state) ?? {})
						.isInDocumentStreamingEnabled,
					traceId: (aiExperienceDecorationPluginKey.getState(state) ?? {}).traceId,
					product: (aiExperienceDecorationPluginKey.getState(state) ?? {}).product,
					promptVersion: fg('platform_editor_ai_promptversion_in_analytics')
						? (aiExperienceDecorationPluginKey.getState(state) ?? {}).promptVersion
						: undefined,
				},
				rovoAgentsPluginState: {
					positions: (rovoAgentsPluginKey.getState(state) ?? {}).positions,
					isBrowseModalOpen: (rovoAgentsPluginKey.getState(state) ?? {}).isBrowseModalOpen ?? false,
					defaultAgent: (rovoAgentsPluginKey.getState(state) ?? {}).defaultAgent,
				},
				isStreaming: expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true)
					? (purePmInEditorDirectStreamingPluginKey.getState(state)?.isStreaming ?? false)
					: (inEditorDirectStreamingPluginKey.getState(state)?.isStreaming ?? false),
				isShowingAcceptRejectScreen:
					(aiExperienceDecorationPluginKey.getState(state) ?? {}).isShowingAcceptRejectScreen ??
					false,
				chunkNumber: expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true)
					? (purePmInEditorDirectStreamingPluginKey.getState(state)?.chunkNumber ?? 0)
					: (inEditorDirectStreamingPluginKey.getState(state)?.chunkNumber ?? 0),
				// Streaming plugin state for React hooks (scroll, mount effects, etc.)
				streamingState: expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true)
					? {
							streamStartPos:
								purePmInEditorDirectStreamingPluginKey.getState(state)?.streamStartPos,
							scrollTargetPos:
								purePmInEditorDirectStreamingPluginKey.getState(state)?.scrollTargetPos,
							streamPhase: purePmInEditorDirectStreamingPluginKey.getState(state)?.streamPhase,
							currentSessionId:
								purePmInEditorDirectStreamingPluginKey.getState(state)?.currentSessionId,
						}
					: undefined,
				selectionContext:
					aiExperienceSelectionPluginKey.getState(state)?.lastSelectionContext ?? undefined,
				hasVisibleContent:
					documentContentStatePluginKey.getState(state)?.hasVisibleContent ?? false,
				consumerEmptyStateOverride:
					documentContentStatePluginKey.getState(state)?.consumerEmptyStateOverride,
				aiSessionPluginState: aiSessionContextPluginKey.getState(state) ?? aiSessionInitialState,
			};
		},
		pluginsOptions: {
			...(isFullEditorAIExperience &&
				!isToolbarAIFCEnabled && {
					selectionToolbar: getSelectionToolbarConfig({
						aiGlobalOptIn: aiOptIn,
						editorPluginAIProvider,
						api,
					}),
				}),
			...(isFullEditorAIExperience && {
				...(isRegisteredSlashCommandEnabled ? {} : { quickInsert }),
				floatingToolbar: getFloatingToolbarConfig({
					aiGlobalOptIn: aiOptIn,
					editorPluginAIProvider,
					api,
				}),
			}),
		},

		contentComponent: isFullEditorAIExperience
			? ({ editorView, wrapperElement, appearance, providerFactory, popupsMountPoint }) => {
					// Keep the paste-menu appearance getter in sync with the editor's live appearance.
					currentAppearance = appearance;

					if (!editorView) {
						return null;
					}

					// We're going to use a different collection of components for the AIFC experience. This should allow for
					// a better SoC and easier cleanup in the future and it will provide clear separation between the two, old/new experiences.
					// This also means we do not need to reference the AIFC feature flag in multiple places throughout the UI.
					const C = editorPluginAIProvider.isInDocumentStreamingEnabled
						? ContentComponentWrapper
						: ContentComponentWrapperLegacy;

					return (
						<>
							{shouldRegisterAIPasteMenu && (
								<SmartCardClientSync clientRef={aiSmartCardClientRef} />
							)}
							<C
								aiOptIn={aiOptIn}
								api={api}
								editorView={editorView}
								wrapperElement={wrapperElement}
								appearance={appearance}
								editorPluginAIProvider={editorPluginAIProvider}
								providerFactory={providerFactory}
								getMentionNameDetails={getMentionNameDetails}
								canInlineStreamChanges={canInlineStreamChanges}
								isInDocumentStreamingEnabled={!!editorPluginAIProvider.isInDocumentStreamingEnabled}
								popupsMountPoint={popupsMountPoint}
								// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
								endExperience={() => {}}
							/>
						</>
					);
				}
			: StreamingContentComponent,

		primaryToolbarComponent:
			isFullEditorAIExperience &&
			!api?.primaryToolbar &&
			!editorPluginAIProvider.hidePrimaryToolbarButton
				? getPrimaryToolbarLegacyComponent({
						api,
						editorPluginAIProvider,
						aiGlobalOptIn: aiOptIn,
					})
				: undefined,
	};
};

// Ignored via go/ees007
// eslint-disable-next-line @atlaskit/editor/enforce-todo-comment-format
/**
 * TODO: Remove this when making a major change as this was originally exported here
 * (Probably when deprecating the legacy plugin)
 * This type is currently used in some products.
 */
// eslint-disable-next-line @atlaskit/editor/no-re-export
export type { AIPlugin } from './aiPluginType';
